import * as React from 'react';

import {useMutation} from '@apollo/react-hooks';

import type {
    DeleteForm as DeleteFormMutation,
    DeleteFormVariables as DeleteFormMutationVariables,
    RestoreForm as RestoreFormMutation,
    RestoreFormVariables as RestoreFormMutationVariables,
    DuplicateForm as DuplicateFormMutation,
    DuplicateFormVariables as DuplicateFormMutationVariables,
    FormDuplicateInputFieldInput,
    GetForms as GetFormsQuery,
    GetFormsVariables as GetFormsQueryVariables,
} from 'nutshell-graphql-types';

import DELETE_FORM from '../mutations/delete-form.graphql';
import DUPLICATE_FORM from '../mutations/duplicate-form.graphql';
import RESTORE_FORM from '../mutations/restore-form.graphql';
import GET_FORMS from '../queries/get-forms.graphql';

type Props = {
    children: (arg1: {
        onDelete: (formId: string, queryVariables?: GetFormsQueryVariables) => Promise<any>;
        onRestore: (formId: string, queryVariables?: GetFormsQueryVariables) => Promise<any>;
        onDuplicate: (
            input: FormDuplicateInputFieldInput,
            queryVariables?: GetFormsQueryVariables
        ) => Promise<any>;
    }) => React.ReactElement;
};

export function FormMutations(props: Props) {
    const [deleteForm] = useMutation<DeleteFormMutation, DeleteFormMutationVariables>(DELETE_FORM);
    const [restoreForm] = useMutation<RestoreFormMutation, RestoreFormMutationVariables>(
        RESTORE_FORM
    );
    const [duplicateForm] = useMutation<DuplicateFormMutation, DuplicateFormMutationVariables>(
        DUPLICATE_FORM
    );

    return props.children({
        onDelete: (formId: string, queryVariables?: GetFormsQueryVariables) => {
            return deleteForm({
                variables: {id: formId},
                update: (proxy, mutationResults) => {
                    const deletedForm =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.formDelete &&
                        mutationResults.data.formDelete.form;

                    if (!deletedForm) return;

                    formCacheUpdate(proxy, deletedForm, true, queryVariables);
                },
            });
        },
        onRestore: (formId: string, queryVariables?: GetFormsQueryVariables) => {
            return restoreForm({
                variables: {id: formId},
                update: (proxy, mutationResults) => {
                    const restoredForm =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.formRestore &&
                        mutationResults.data.formRestore.form;

                    if (!restoredForm) return;

                    formCacheUpdate(proxy, restoredForm, false, queryVariables);
                },
            });
        },
        onDuplicate: (
            input: FormDuplicateInputFieldInput,
            queryVariables?: GetFormsQueryVariables
        ) =>
            duplicateForm({
                variables: {input},
                update: (proxy, mutationResults) => {
                    const newForm =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.formDuplicate &&
                        mutationResults.data.formDuplicate.form;

                    if (!newForm) return;

                    formCacheUpdate(proxy, newForm, false, queryVariables);
                },
            }),
    });
}

/**
 * This will handle updating our local cache when we archive or restore a
 * form. It correctly handles updating both the archived and unarchived
 * caches.
 *
 * @param {DataProxy} proxy The mutation update proxy
 * @param {Form} form The form that was mutated
 * @param {boolean} isDeleting True if we are deleting the given form,
 * false if we are restoring it.
 */
const formCacheUpdate = (
    // @ts-expect-error
    proxy,
    // @ts-expect-error
    form,
    isDeleting: boolean,
    queryVariables?: GetFormsQueryVariables
) => {
    // Passing a value for `isArchived` here isn't really needed because we
    // overwrite it when writing to the cache below, but hey I made it required
    const variables = queryVariables || {};

    try {
        // Update our archived cache
        // This isn't the best variable name, but its the cache we're removing
        // an form from.
        const cacheResultRemoving: GetFormsQuery | null | undefined = proxy.readQuery({
            query: GET_FORMS,
            variables: {...variables, archived: !isDeleting},
        });

        if (cacheResultRemoving && cacheResultRemoving.forms) {
            proxy.writeQuery({
                query: GET_FORMS,
                variables: {...variables, archived: !isDeleting},
                data: {
                    forms: {
                        ...cacheResultRemoving.forms,
                        edges: cacheResultRemoving.forms.edges.filter((edge) => {
                            return edge.node.id !== form.id;
                        }),
                    },
                },
            });
        }
    } catch {
        // If the above query doesn't exist in the cache, Apollo will throw an
        // error (why it can't just return null is beyond me). If that happens,
        // we need to "catch" the error. But we don't want to really do anything
        // with it. The just means we haven't performed this query yet. So, an
        // empty catch block
    }

    // Update our unarchived cache
    // Again, not the best name. We're adding an form to this cache
    try {
        const cacheResultAdding: GetFormsQuery | null | undefined = proxy.readQuery({
            query: GET_FORMS,
            variables: {...variables, archived: isDeleting},
        });

        if (!cacheResultAdding || !cacheResultAdding.forms) {
            return;
        }

        proxy.writeQuery({
            query: GET_FORMS,
            variables: {...variables, archived: isDeleting},
            data: {
                forms: {
                    ...cacheResultAdding.forms,
                    edges: [
                        {
                            cursor: form.id,
                            node: form,
                            __typename: 'FormsEdge',
                        },
                    ].concat(cacheResultAdding.forms.edges),
                },
            },
        });
    } catch {
        // If the above query doesn't exist in the cache, Apollo will throw an
        // error (why it can't just return null is beyond me). If that happens,
        // we need to "catch" the error. But we don't want to really do anything
        // with it. The just means we haven't performed this query yet. So, an
        // empty catch block
    }
};
